import * as pdfjsLib from "pdfjs-dist";
// Worker aus derselben pdfjs-dist-Version wie die API (vermeidet Version-Mismatch)
import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.mjs?url";
import type { DetectedFloor, DetectedRoom } from "./initData";
import { callClaudeWorker } from "./claudeWorker";

function ensurePdfjsWorkerSrc(): void {
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;
  }
}

// lange Kante der gerenderten Planseite – hoch genug, damit Raumbeschriftungen lesbar bleiben,
// aber klein genug, um die Anfrage an das Vision-Modell schlank zu halten.
const MAX_EDGE_PX = 2000;

async function renderPdfPageToJpeg(page: any): Promise<{ mediaType: string; base64: string }> {
  const viewport1 = page.getViewport({ scale: 1 });
  const scale = MAX_EDGE_PX / Math.max(viewport1.width, viewport1.height);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;

  const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
  return { mediaType: "image/jpeg", base64: dataUrl.split(",")[1] };
}

const EXTRACT_PROMPT = `Du bist ein Assistent für Elektro-/KNX-Planung. Die beigefügte Seite zeigt eine oder mehrere Etagen eines Gebäude-Grundrisses. Mehrere Etagen auf einer Seite sind immer in irgendeiner Form beschriftet (z.B. "EG" und "OG" nebeneinander, per Legende oder Überschrift getrennt) – erkenne jede Etage auf der Seite einzeln.

Regeln pro Etage:
- Nutze die Beschriftung der Etage (z.B. EG, OG, DG, KG, UG, "Erdgeschoss"), falls vorhanden, sonst "Etage".
- Erfasse NUR Innenräume innerhalb der Gebäudehülle (durch Wände umschlossene Räume). Erfasse KEINE Außenbereiche als eigenen Raum, auch wenn sie beschriftet sind – z.B. Terrasse, Balkon, Garten, Einfahrt, Zufahrt, Pool, Vorplatz. Eine Garage gilt als Innenraum und wird erfasst, sofern sie ein umschlossener Raum ist.
- Erfasse jeden Raum mit seinem beschrifteten Namen. Ist ein Raum unbeschriftet, leite den Namen aus Symbolen ab (WC-Schüssel, Badewanne, Küchenzeile, Treppe ...).
- Ordne jedem Raum einen Typ zu aus genau dieser Liste: Wohnen, Schlafen, Kind, Küche, Bad, WC, Flur, Abstellraum, HWR, Technik, Büro, Garage, Treppenhaus, Sonstiges.
- Lies die Fläche in m² nur ab, wenn sie im Plan beschriftet ist. Niemals schätzen – ohne Beschriftung ist "flaeche_m2" null.
- Falls die Seite keinen Grundriss zeigt (z.B. Deckblatt, Schnitt, Ansicht), liefere ein leeres "etagen"-Array zurück.

Antworte AUSSCHLIESSLICH mit validem JSON in genau dieser Form, ohne Markdown, ohne Codeblöcke:
{ "etagen": [ { "name": string, "raeume": [ { "name": string, "typ": string, "flaeche_m2": number|null } ] } ] }`;

async function callClaude(image: { mediaType: string; base64: string }): Promise<string> {
  return callClaudeWorker(
    [
      { type: "image", source: { type: "base64", media_type: image.mediaType, data: image.base64 } },
      { type: "text", text: EXTRACT_PROMPT },
    ],
    4096,
  );
}

function parseExtractionResponse(text: string, pdfPageNumber: number): DetectedFloor[] {
  const clean = text.replace(/```json|```/g, "").trim();
  if (!clean) {
    return [];
  }
  const parsed = JSON.parse(clean);
  const etagen = parsed.etagen || [];
  return etagen
    .filter((et: any) => et && et.name)
    .map((et: any) => ({
      name: String(et.name),
      pdfPageNumber,
      raeume: (et.raeume || [])
        .filter((r: any) => r && r.name)
        .map(
          (r: any): DetectedRoom => ({
            name: String(r.name),
            typ: r.typ ? String(r.typ) : "",
            flaeche_m2: typeof r.flaeche_m2 === "number" ? r.flaeche_m2 : null,
          }),
        ),
    }));
}

export async function extractFloorsAndRoomsFromPdf(
  file: File,
  onProgress?: (current: number, total: number) => void,
): Promise<DetectedFloor[]> {
  ensurePdfjsWorkerSrc();
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

  const detectedFloors: DetectedFloor[] = [];
  // Seiten werden bewusst sequenziell (nicht parallel) verarbeitet, um einzelne
  // Seitenfehler isoliert zu behandeln und den Analyse-Server nicht zu überlasten.
  for (let i = 1; i <= pdf.numPages; i++) {
    onProgress?.(i, pdf.numPages);
    try {
      const page = await pdf.getPage(i);
      const image = await renderPdfPageToJpeg(page);
      const data = await callClaude(image);
      detectedFloors.push(...parseExtractionResponse(data, i));
    } catch (err) {
      // Fehler auf der ersten Seite deuten meist auf ein systemisches Problem hin
      // (Server nicht erreichbar, Netzwerk) – das soll dem Nutzer angezeigt werden.
      if (i === 1) {
        throw err;
      }
      // spätere Einzelseiten überspringen – der Assistent darf durch eine Fehlseite nicht blockiert werden
      continue;
    }
  }
  return detectedFloors;
}
